import { useState, useMemo, useEffect } from 'react';

import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import QuestionCard from './QuestionCard';

const MIN_HOURS_AHEAD = 2; // الحد الأدنى للحجز: ساعتان من الوقت الحالي

// أيام الأسبوع بالعربية (0=الأحد، 1=الإثنين، ...)
const ARABIC_DAYS = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];

// أسماء الشهور بالعربية
const ARABIC_MONTHS = ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];

const parseDateStr = (dateStr) => {
  if (!dateStr) return { day: '', month: '', year: '' };
  const [y, m, d] = dateStr.split('-');
  return { day: String(parseInt(d, 10)), month: String(parseInt(m, 10)), year: y };
};

// ربط الساعات بالفترات (صباح/ظهر/مساء) حسب التوفر الأسبوعي
const SLOT_TO_PERIOD = (hour) => {
  if (hour >= 8 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour <= 23) return 'evening';
  return null;
};

const getTodayStr = () => {
  const t = new Date();
  const y = t.getFullYear();
  const m = String(t.getMonth() + 1).padStart(2, '0');
  const d = String(t.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const DateTimeStep = ({ formData, onUpdate, isDirectMode }) => {
  const initialDate = formData?.date || getTodayStr();
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [selectedTime, setSelectedTime] = useState(formData?.time || '');
  const [dateParts, setDateParts] = useState(() => parseDateStr(initialDate));
  const [busyDates, setBusyDates] = useState([]);
  const [busyDateError, setBusyDateError] = useState('');

  // ضبط تاريخ اليوم تلقائياً في الـ parent عند أول تحميل إذا لم يكن محدداً
  useEffect(() => {
    if (!formData?.date) {
      onUpdate({ date: getTodayStr(), time: formData?.time || '' });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const d = formData?.date || getTodayStr();
    setSelectedDate(d);
    setSelectedTime(formData?.time || '');
    setDateParts(parseDateStr(d));
  }, [formData?.date, formData?.time]);

  // في الحجز المباشر: جلب التواريخ المشغولة للمصور المختار
  useEffect(() => {
    if (!isDirectMode) return;
    const photographerId =
      formData?.photographerId ||
      formData?.preselectedPhotographerId ||
      formData?.directPhotographerProfile?.id;
    if (!photographerId) return;

    // استخدام RPC بـ SECURITY DEFINER لتجاوز RLS (العميل لا يرى حجوزات الآخرين)
    supabase
      .rpc('get_photographer_busy_dates', { p_photographer_id: photographerId })
      .then(({ data }) => {
        if (data) {
          setBusyDates(data.map((b) => b.booking_date).filter(Boolean));
        }
      })
      .catch(() => {
        // non-blocking — if fetch fails just show no restrictions
      });
  }, [
    isDirectMode,
    formData?.photographerId,
    formData?.preselectedPhotographerId,
    formData?.directPhotographerProfile?.id,
  ]);

  // فحص التاريخ الحالي مقابل busyDates عند تحميلها أو تغيّر التاريخ
  // يعالج: draft محفوظ مسبقاً + تغيّر المصور أثناء الحجز
  useEffect(() => {
    if (!isDirectMode || !formData?.date || busyDates.length === 0) return;
    if (busyDates.includes(formData.date)) {
      setBusyDateError('المصور لديه حجز مؤكد في هذا التاريخ، يرجى اختيار تاريخ آخر');
      setSelectedDate('');
      setDateParts({ day: '', month: '', year: '' });
      onUpdate({ date: '', time: '' });
    }
  }, [formData?.date, busyDates]); // eslint-disable-line react-hooks/exhaustive-deps

  // الحد الأدنى للتاريخ: اليوم بالتوقيت المحلي (نسمح باليوم إذا كان الوقت بعد ساعتين)
  const getMinDate = () => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate?.setMonth(maxDate?.getMonth() + 6);
    return maxDate?.toISOString()?.split('T')?.[0];
  };

  // هل التاريخ المختار هو اليوم؟
  const isTodaySelected = (dateStr) => {
    return dateStr && dateStr === new Date().toISOString().split('T')[0];
  };

  // هل هذا الوقت صالح للحجز؟ (ساعتان على الأقل من الآن إذا كان اليوم)
  const isSlotAllowed = (slotValue) => {
    if (!isTodaySelected(selectedDate)) return true;
    const [h, m] = slotValue.split(':').map(Number);
    const slotTime = new Date();
    slotTime.setHours(h, m, 0, 0);
    const minAllowed = new Date(Date.now() + MIN_HOURS_AHEAD * 60 * 60 * 1000);
    return slotTime >= minAllowed;
  };

  // بناء قوائم يوم/شهر/سنة مع استبعاد التواريخ الخارج عن النطاق
  const [minY, minM, minD] = getMinDate().split('-').map(Number);
  const [maxY, maxM, maxD] = getMaxDate().split('-').map(Number);

  const availableYears = Array.from({ length: maxY - minY + 1 }, (_, i) => minY + i);

  const getAvailableMonths = (year) => {
    const y = Number(year);
    if (!y) return Array.from({ length: 12 }, (_, i) => i + 1);
    const start = y === minY ? minM : 1;
    const end   = y === maxY ? maxM : 12;
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const getAvailableDays = (year, month) => {
    const y = Number(year);
    const m = Number(month);
    const total = (y && m) ? new Date(y, m, 0).getDate() : 31;
    const start = (y === minY && m === minM) ? minD : 1;
    const end   = (y === maxY && m === maxM) ? Math.min(maxD, total) : total;
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const handleDatePartChange = (part, value) => {
    const next = { ...dateParts, [part]: value };
    // تصحيح اليوم إذا تجاوز عدد أيام الشهر الجديد
    if ((part === 'year' || part === 'month') && next.year && next.month) {
      const maxDay = new Date(Number(next.year), Number(next.month), 0).getDate();
      if (Number(next.day) > maxDay) next.day = '';
    }
    setDateParts(next);
    if (next.day && next.month && next.year) {
      const d = String(next.day).padStart(2, '0');
      const m = String(next.month).padStart(2, '0');
      handleDateChange({ target: { value: `${next.year}-${m}-${d}` } });
    }
  };

  const handleDateChange = (e) => {
    const date = e?.target?.value;
    const todayStr = new Date().toISOString().split('T')[0];
    // SAFE: رفض التاريخ في الماضي
    if (date && date < todayStr) return;
    // رفض التاريخ إذا كان المصور مشغولاً فيه (الحجز المباشر)
    if (date && busyDates.includes(date)) {
      setBusyDateError('المصور لديه حجز مؤكد في هذا التاريخ، يرجى اختيار تاريخ آخر');
      setDateParts({ day: '', month: '', year: '' }); // إعادة لفارغ — لا ترجع للتاريخ المشغول
      return;
    }
    setBusyDateError('');
    setSelectedDate(date);
    let timeToKeep = selectedTime;
    if (selectedTime && date === todayStr) {
      const [h, m] = selectedTime.split(':').map(Number);
      const slotTime = new Date();
      slotTime.setHours(h, m, 0, 0);
      const minAllowed = new Date(Date.now() + MIN_HOURS_AHEAD * 60 * 60 * 1000);
      if (slotTime < minAllowed) {
        timeToKeep = '';
        setSelectedTime('');
      }
    }
    // الحجز المباشر: إذا غيّر التاريخ والوقت المختار غير متاح للتاريخ الجديد → مسح الوقت
    if (timeToKeep && isDirectMode && formData?.directPhotographerProfile?.weeklyAvailability && date) {
      const weekly = formData.directPhotographerProfile.weeklyAvailability ?? formData.directPhotographerProfile.weekly_availability;
      if (weekly && typeof weekly === 'object') {
        const d = new Date(date + 'T12:00:00');
        const dayName = ARABIC_DAYS[d.getDay()];
        const daySchedule = weekly[dayName];
        if (daySchedule) {
          const [h] = timeToKeep.split(':').map(Number);
          const period = SLOT_TO_PERIOD(h);
          if (period && !(daySchedule[period] ?? true)) {
            timeToKeep = '';
            setSelectedTime('');
          }
        }
      }
    }
    onUpdate({ date, time: timeToKeep || '' });
  };

  const baseTimeSlots = [
    { value: '09:00', label: '9:00 صباحاً', hour: 9 },
    { value: '10:00', label: '10:00 صباحاً', hour: 10 },
    { value: '11:00', label: '11:00 صباحاً', hour: 11 },
    { value: '12:00', label: '12:00 ظهراً', hour: 12 },
    { value: '13:00', label: '1:00 ظهراً', hour: 13 },
    { value: '14:00', label: '2:00 ظهراً', hour: 14 },
    { value: '15:00', label: '3:00 عصراً', hour: 15 },
    { value: '16:00', label: '4:00 عصراً', hour: 16 },
    { value: '17:00', label: '5:00 مساءً', hour: 17 },
    { value: '18:00', label: '6:00 مساءً', hour: 18 },
    { value: '19:00', label: '7:00 مساءً', hour: 19 },
    { value: '20:00', label: '8:00 مساءً', hour: 20 },
    { value: '21:00', label: '9:00 مساءً', hour: 21 },
    { value: '22:00', label: '10:00 مساءً', hour: 22 },
    { value: '23:00', label: '11:00 مساءً', hour: 23 },
  ];

  // حالة توفر المصور في اليوم المختار (null = لا تنبيه، 'full' = اليوم كاملاً غير متاح، 'partial' = بعض الفترات غير متاحة)
  const weeklyAvailabilityHint = useMemo(() => {
    const weekly = formData?.directPhotographerProfile?.weeklyAvailability ?? formData?.directPhotographerProfile?.weekly_availability;
    if (!isDirectMode || !weekly || typeof weekly !== 'object' || !formData?.date) return null;
    const d = new Date(formData.date + 'T12:00:00');
    const dayName = ARABIC_DAYS[d.getDay()];
    const daySchedule = weekly[dayName];
    if (!daySchedule) return null;
    const periods = ['morning', 'afternoon', 'evening'];
    const availableCount = periods.filter(p => daySchedule[p] ?? true).length;
    if (availableCount === 0) return 'full';
    if (availableCount < periods.length) return 'partial';
    return null;
  }, [isDirectMode, formData?.directPhotographerProfile?.weeklyAvailability, formData?.directPhotographerProfile?.weekly_availability, formData?.date]);

  // الحجز المباشر: تصفية الأوقات حسب التوفر الأسبوعي للمصور (SAFE: إن لم يوجد توفر نعرض الكل)
  const availableTimeSlots = useMemo(() => {
    const weekly = formData?.directPhotographerProfile?.weeklyAvailability ?? formData?.directPhotographerProfile?.weekly_availability;
    if (!isDirectMode || !weekly || typeof weekly !== 'object' || !formData?.date) {
      return baseTimeSlots.map((s) => ({ ...s, available: true }));
    }
    const d = new Date(formData.date + 'T12:00:00');
    const dayIndex = d.getDay();
    const dayName = ARABIC_DAYS[dayIndex];
    const daySchedule = weekly[dayName];
    if (!daySchedule) return baseTimeSlots.map((s) => ({ ...s, available: true }));
    return baseTimeSlots.map((slot) => {
      const period = SLOT_TO_PERIOD(slot.hour);
      const isAvailable = period ? (daySchedule[period] ?? true) : true;
      return { ...slot, available: isAvailable };
    });
  }, [isDirectMode, formData?.directPhotographerProfile?.weeklyAvailability, formData?.directPhotographerProfile?.weekly_availability, formData?.date]);

  return (
    <QuestionCard
      icon="Calendar"
      title="متى تريد جلسة التصوير؟"
      description="اختر التاريخ والوقت المناسب لك"
    >
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            التاريخ
          </label>
          <div className="flex gap-2">
            {/* يوم */}
            <select
              value={dateParts.day}
              onChange={e => handleDatePartChange('day', e.target.value)}
              dir="ltr"
              className="flex-1 px-3 py-3 rounded-xl border-2 border-border bg-background text-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-center"
            >
              <option value="">يوم</option>
              {getAvailableDays(dateParts.year, dateParts.month).map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            {/* شهر */}
            <select
              value={dateParts.month}
              onChange={e => handleDatePartChange('month', e.target.value)}
              dir="ltr"
              className="flex-[1.5] px-3 py-3 rounded-xl border-2 border-border bg-background text-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-center"
            >
              <option value="">شهر</option>
              {getAvailableMonths(dateParts.year).map(m => (
                <option key={m} value={m}>{ARABIC_MONTHS[m - 1]}</option>
              ))}
            </select>
            {/* سنة */}
            <select
              value={dateParts.year}
              onChange={e => handleDatePartChange('year', e.target.value)}
              dir="ltr"
              className="flex-1 px-3 py-3 rounded-xl border-2 border-border bg-background text-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-center"
            >
              <option value="">سنة</option>
              {availableYears.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          {isTodaySelected(formData?.date) && (
            <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
              <Icon name="Clock" size={12} />
              لحجز اليوم يجب اختيار وقت بعد ساعتين على الأقل من الآن
            </p>
          )}
          {busyDateError && (
            <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
              <Icon name="AlertCircle" size={12} />
              {busyDateError}
            </p>
          )}
          {!busyDateError && weeklyAvailabilityHint === 'full' && (
            <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
              <Icon name="CalendarX" size={12} />
              هذا المصور خارج أوقات التوفر في هذا اليوم
            </p>
          )}
          {!busyDateError && weeklyAvailabilityHint === 'partial' && (
            <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
              <Icon name="Clock" size={12} />
              اختر من الأوقات المتاحة لهذا المصور في هذا اليوم
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            الوقت المفضل
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {availableTimeSlots?.map((slot) => {
              const blocked = !slot.available || !isSlotAllowed(slot.value);
              const isSelected = formData?.time === slot?.value;
              return (
                <button
                  key={slot?.value}
                  onClick={() => {
                    if (blocked) return;
                    setSelectedTime(slot.value);
                    onUpdate({ time: slot?.value });
                  }}
                  disabled={blocked}
                  title={!slot.available ? (isDirectMode ? 'غير متاح في توفر المصور' : 'غير متاح') : !isSlotAllowed(slot.value) ? 'الوقت مضى أو أقل من ساعتين' : ''}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${
                    blocked
                      ? 'border-border bg-slate-50 opacity-40 cursor-not-allowed'
                      : isSelected
                      ? 'border-primary bg-primary/10 shadow-sm'
                      : 'border-border bg-background hover:border-primary hover:bg-primary/5'
                  }`}
                >
                  <div className="text-sm font-medium text-foreground">
                    {slot?.label}
                  </div>
                  {isSelected && !blocked && (
                    <Icon name="CheckCircle2" size={16} color="var(--color-primary)" className="mx-auto mt-1" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </QuestionCard>
  );
};

export default DateTimeStep;